'use client';

// Third Party
import * as React from 'react';

// Utils
import type { ListingBySlugPartial } from '@utils/imperative-node-queries';
import { zIndex } from '@utils/constants';
import { formatWholeDollarAmount } from '@utils/money';
import { useQuotes } from './quotes-context';
import { QuoteItemizedType } from '@gql';

// UI
import { Typography } from '@components/typography';
import { PrimaryBreakdown } from '@components/listing-quote-breakdown/listing-quote-breakdown';

type Props = { listing?: ListingBySlugPartial };

export const QuoteBreakdownInfo: React.FC<Props> = () => {
  const { numberOfNights, datesPresent, quoteData } = useQuotes();

  const getOrder = (type: QuoteItemizedType) => {
    switch (type) {
      case QuoteItemizedType.Base:
        return 1;
      case QuoteItemizedType.Fee:
        return 2;
      case QuoteItemizedType.Other:
        return 3;
      case QuoteItemizedType.Discount:
        return 4;
      default:
        return 5;
    }
  };

  const quoteBreakdownItems =
    quoteData?.bookingQuoteByListingAndDates.__typename == 'BookingQuote'
      ? quoteData.bookingQuoteByListingAndDates.quoteItems
      : [];
  // Sorting the items based on the type order
  const quoteItems = [...quoteBreakdownItems].sort((a, b) => {
    return getOrder(a.type) - getOrder(b.type);
  });

  const categorizedQuoteItems = quoteItems.reduce(
    (acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    },
    {} as Record<QuoteItemizedType, typeof quoteItems>
  );

  const feeCents = categorizedQuoteItems[
    QuoteItemizedType.Fee || QuoteItemizedType.Other
  ]?.reduce((acc, item) => acc + item.costCents, 0);

  return (
    <div
      className="bg-white flex flex-col justify-between w-full h-full rounded-t-xl space-y-2 pt-2 pb-4 shadow-2xl"
      style={{ zIndex: zIndex.dropdown }}
    >
      {datesPresent &&
        quoteData &&
        quoteData.bookingQuoteByListingAndDates &&
        quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote' &&
        quoteData.bookingQuoteByListingAndDates.quoteItems?.length > 0 &&
        quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents && (
          <div className="mt-2 w-full text-gray-dark px-4">
            <div className="flex flex-col justify-between w-full h-full">
              <div className="relative space-y-2">
                {quoteItems?.length === 0 && (
                  <div className="flex w-full justify-between space-x-2">
                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium text-xs"
                    >
                      {formatWholeDollarAmount(
                        Math.round(
                          quoteData.bookingQuoteByListingAndDates
                            .totalWithoutTaxesCents / numberOfNights
                        )
                      )}{' '}
                      x {numberOfNights} nights
                    </Typography>

                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium"
                    >
                      {formatWholeDollarAmount(
                        quoteData.bookingQuoteByListingAndDates
                          .totalWithoutTaxesCents
                      )}
                    </Typography>
                  </div>
                )}

                {categorizedQuoteItems[QuoteItemizedType.Base]?.length > 0 && (
                  <div className="flex w-full justify-between space-x-2">
                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium text-xs"
                    >
                      {formatWholeDollarAmount(
                        Math.round(
                          (categorizedQuoteItems[QuoteItemizedType.Base][0]
                            .costCents +
                            (feeCents || 0)) /
                            numberOfNights
                        )
                      )}{' '}
                      x {numberOfNights} nights
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      className="font-medium"
                    >
                      {formatWholeDollarAmount(
                        categorizedQuoteItems[QuoteItemizedType.Base][0]
                          .costCents + (feeCents || 0)
                      )}
                    </Typography>
                  </div>
                )}

                {categorizedQuoteItems[QuoteItemizedType.Discount]?.map(
                  (quoteItem, index) => (
                    <div key={index}>
                      <PrimaryBreakdown quoteBreakdownItem={quoteItem} />
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
